import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, Check } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err) || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // Using window.location.origin ensures auth works across all environments (preview, production, custom domains)
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-brand-bg" data-testid="login-page">
      <div className="hidden md:flex md:w-1/2 relative">
        <img src="https://images.pexels.com/photos/4312847/pexels-photo-4312847.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2">
            <span className="relative inline-flex items-center justify-center h-7 w-7">
              <Heart className="h-7 w-7 text-brand-gold" fill="#C5A059" strokeWidth={1.2} />
              <Check className="absolute h-3.5 w-3.5 text-brand-ink" strokeWidth={3.2} style={{ top: "44%", left: "50%", transform: "translate(-50%, -50%)" }} />
            </span>
            <span className="font-serif text-3xl tracking-wide">Right<span className="italic text-brand-gold"> Match</span></span>
          </Link>
          <div>
            <p className="overline text-brand-gold">Welcome back</p>
            <h1 className="font-serif text-5xl mt-3 leading-tight">Continue your journey to <em className="text-brand-gold">forever</em>.</h1>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="md:hidden flex items-center gap-2 mb-8">
            <LogoMark size="text-2xl" />
          </Link>
          <p className="overline">Sign in</p>
          <h2 className="font-serif text-4xl text-brand-ink mt-2">Welcome back</h2>
          <p className="text-sm text-brand-inkLight mt-2">Don't have an account? <Link to="/register" className="text-brand-maroon hover:underline">Create one</Link></p>

          <button onClick={googleLogin} className="mt-8 w-full border border-brand-line bg-white py-3 rounded-md hover:bg-brand-muted text-sm flex items-center justify-center gap-3" data-testid="login-google-btn">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-brand-line" />
            <span className="text-xs text-brand-inkMute uppercase tracking-[0.2em]">or</span>
            <div className="flex-1 h-px bg-brand-line" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="overline">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-elegant mt-1" data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="overline">Password</label>
                <Link to="/forgot-password" className="text-xs text-brand-maroon hover:underline" data-testid="login-forgot">Forgot?</Link>
              </div>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-elegant mt-1" data-testid="login-password" />
            </div>
            {error && <p className="text-sm text-brand-maroon">{error}</p>}
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit">{loading ? "Signing in..." : "Sign in"}</button>
          </form>
        </div>
      </div>
    </div>
  );
}
